import { ArrowLeft, ArrowRight, Check, Sparkles } from "lucide-react";

/**
 * Shared chrome for every wizard step.
 *
 * Layout: a 2-column on desktop, single-column on mobile. Left = step
 * marketing copy + step indicator dots, right = the editable form.
 *
 * Children render the actual form/UI for the step. The footer renders
 * navigation (Back / Next / optional Skip) so each step only worries
 * about its own data.
 */
export default function StepLayout({
  stepIndex,             // 0-based current step
  totalSteps,            // total steps in the wizard
  title,
  subtitle,
  description,
  illustration,          // optional ReactNode displayed in the left rail
  errorMsg,
  okMsg,
  children,
  // Footer behaviour
  onBack,                // omit to hide Back button
  onNext,                // primary action
  nextLabel = "Save & continue",
  nextDisabled = false,
  busy = false,
  onFinishLater,         // bottom-left "Save & finish later" text-link
  isFinal = false,       // last step → next button label changes to "Finish"
}) {
  return (
    <div className="min-h-screen flex flex-col" data-testid={`wizard-step-${stepIndex + 1}`}>
      {/* Top progress strip + brand */}
      <header className="flex items-center justify-between gap-4 px-5 sm:px-10 py-4 border-b" style={{ borderColor: "var(--vb-border)" }}>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full grid place-items-center" style={{ background: "var(--vb-text-2)" }}>
            <Sparkles size={15} color="white" />
          </div>
          <span className="vb-heading text-base" style={{ color: "var(--vb-text-2)" }}>Vanguard Beacon — Setup</span>
        </div>
        <div className="flex items-center gap-1.5" data-testid="wizard-progress-dots">
          {Array.from({ length: totalSteps }, (_, i) => (
            <div
              key={i}
              className="h-1.5 rounded-full transition-all"
              style={{
                width: i === stepIndex ? 28 : 12,
                background:
                  i < stepIndex ? "var(--vb-accent)" :
                    i === stepIndex ? "var(--vb-text-2)" :
                      "var(--vb-border)",
              }}
            />
          ))}
        </div>
      </header>

      {/* Body: left rail (intro) + right form */}
      <main className="flex-1 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        <aside
          className="px-6 sm:px-10 py-8 lg:py-12 lg:sticky lg:top-0 lg:max-h-screen overflow-y-auto"
          style={{ background: "var(--vb-bg-2)" }}
          data-testid={`wizard-rail-${stepIndex + 1}`}
        >
          <div className="max-w-md mx-auto lg:mx-0">
            <div className="text-xs uppercase tracking-[0.2em] mb-2 opacity-60">
              Step {stepIndex + 1} of {totalSteps}
            </div>
            <h1 className="vb-heading text-3xl sm:text-4xl mb-2 leading-tight" style={{ color: "var(--vb-text-2)" }}>
              {title}
            </h1>
            {subtitle && (
              <p className="text-sm opacity-80 mb-4">{subtitle}</p>
            )}
            {description && (
              <div className="text-sm opacity-70 leading-relaxed">{description}</div>
            )}
            {illustration && (
              <div className="mt-6">{illustration}</div>
            )}
          </div>
        </aside>

        <section className="px-5 sm:px-10 py-8 lg:py-12" data-testid={`wizard-body-${stepIndex + 1}`}>
          <div className="max-w-2xl mx-auto">
            {errorMsg && (
              <div className="vb-pill err w-full mb-4" data-testid={`wizard-err-${stepIndex + 1}`}>
                {errorMsg}
              </div>
            )}
            {okMsg && (
              <div className="vb-pill ok w-full mb-4" data-testid={`wizard-ok-${stepIndex + 1}`}>
                {okMsg}
              </div>
            )}
            {children}
          </div>
        </section>
      </main>

      {/* Footer nav */}
      <footer
        className="flex items-center justify-between gap-3 flex-wrap px-5 sm:px-10 py-4 border-t"
        style={{ borderColor: "var(--vb-border)", background: "var(--vb-bg-2)" }}
      >
        <div className="flex items-center gap-3">
          {onBack && (
            <button
              type="button" className="vb-btn ghost"
              onClick={onBack} disabled={busy}
              data-testid="wizard-back-btn"
            >
              <ArrowLeft size={14} /> Back
            </button>
          )}
          {onFinishLater && (
            <button
              type="button"
              onClick={onFinishLater}
              disabled={busy}
              className="text-xs underline opacity-70 hover:opacity-100"
              data-testid="wizard-finish-later-btn"
            >
              Save & finish later
            </button>
          )}
        </div>
        <button
          type="button" className="vb-btn"
          onClick={onNext} disabled={nextDisabled || busy}
          data-testid="wizard-next-btn"
        >
          {busy ? "Saving…" : (
            <>
              {isFinal ? <><Check size={14} /> {nextLabel}</> : <>{nextLabel} <ArrowRight size={14} /></>}
            </>
          )}
        </button>
      </footer>
    </div>
  );
}
